import { notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdArrowBackIos,
  MdCheckCircle,
  MdLock,
  MdOutlineCall,
  MdOutlineLocationOn,
  MdOutlineMail,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM } from "utils/constants";

const FORMULA_LABELS = { solo: "Téléconsultation Solo", duo: "Téléconsultation Duo", famille: "Téléconsultation Famille" };

const Contract = ({ project, onNext, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const totalPrice = get(project, "fields.totalPrice", 0);
  const selectedTeleconsultation = get(project, "fields.selectedTeleconsultation", "solo");
  const [nextLoading, setNextLoading] = useState(false);

  const [form, setForm] = useState({
    zipCode: "",
    city: "",
    address: "",
    email: get((get(project, "contact.telecoms", []) || []).find(({ system }) => system === CONTACT_POINT_SYSTEM.EMAIL), "value", ""),
    phone: get((get(project, "contact.telecoms", []) || []).find(({ system }) => system === CONTACT_POINT_SYSTEM.PHONE), "value", ""),
    accountHolder: "",
    iban: "",
    bic: "",
    acceptCGV: false,
    acceptSepa: false,
    optinMarketing: false,
    optinPartners: false,
  });

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const isValid = form.zipCode && form.city && form.address && form.email && form.phone && form.accountHolder && form.iban && form.bic && form.acceptCGV && form.acceptSepa;

  const handleSubmit = () => {
    if (!isValid) {
      notification.warning({ message: "Veuillez remplir tous les champs obligatoires" });
      return;
    }
    setNextLoading(true);
    onNext({
      payload: {
        locked: true,
        fields: { ...project.fields, subscription: form, subscriptionCompleted: true },
      },
      onCompleted: () => setNextLoading(false),
      onError: () => setNextLoading(false),
    });
  };

  return (
    <>
      <Header key="header" title={DETAIL.HEADER.TITLE} />
      <div className="contact-form" key="form_area">
        <div className="contact-form__content">
          <Form title="Souscription et Paiement" description="Finalisation avec coordonnées bancaires et consentements RGPD">
            <div className="contact-stepper" key="ADDRESS">
              <div className="contact-stepper__header">
                <div className="contact-stepper__header--counter"><span>1</span></div>
                <div className="contact-stepper__header--content">
                  <h1 className="contact-stepper__header--content--title"><span>Adresse</span></h1>
                  <p className="contact-stepper__header--content--description">Complétez vos informations d'adresse</p>
                </div>
              </div>
              <div className="contact-stepper__content">
                <Input icon={<MdOutlineLocationOn size={22} />} label="Code postal *" placeholder="75001" value={form.zipCode} onChange={({ target: { value } }) => updateForm("zipCode", value)} />
                <Input icon={<MdOutlineLocationOn size={22} />} label="Ville *" placeholder="Paris" value={form.city} onChange={({ target: { value } }) => updateForm("city", value)} />
                <Input icon={<MdOutlineLocationOn size={22} />} label="Adresse *" placeholder="56 boulevard..." value={form.address} onChange={({ target: { value } }) => updateForm("address", value)} />
                <Input icon={<MdOutlineMail size={22} />} label="Email *" placeholder="email@example.com" value={form.email} onChange={({ target: { value } }) => updateForm("email", value)} />
                <Input icon={<MdOutlineCall size={22} />} label="Téléphone portable *" placeholder="06 12 34 56 78" value={form.phone} onChange={({ target: { value } }) => updateForm("phone", value)} />
              </div>
            </div>

            <div className="contact-stepper" key="BANKING">
              <div className="contact-stepper__header">
                <div className="contact-stepper__header--counter"><span>2</span></div>
                <div className="contact-stepper__header--content">
                  <h1 className="contact-stepper__header--content--title"><span>Coordonnées bancaires</span></h1>
                  <p className="contact-stepper__header--content--description">Pour le prélèvement SEPA mensuel</p>
                </div>
              </div>
              <div className="contact-stepper__content">
                <Input icon={<MdLock size={22} />} label="Titulaire du compte *" placeholder="Jane Doe" value={form.accountHolder} onChange={({ target: { value } }) => updateForm("accountHolder", value)} />
                <Input icon={<MdLock size={22} />} label="IBAN *" placeholder="FR76 1890 0000 0000 0000 00" value={form.iban} onChange={({ target: { value } }) => updateForm("iban", value)} />
                <Input icon={<MdLock size={22} />} label="BIC / SWIFT *" placeholder="BCA" value={form.bic} onChange={({ target: { value } }) => updateForm("bic", value)} />
                <Alert showIcon type="info" icon={<MdLock size={16} />} message={`Vos coordonnées bancaires sont chiffrées et sécurisées. Le prélèvement sera effectué mensuellement pour un montant de ${totalPrice.toFixed(2)} €`} />
              </div>
            </div>

            <div className="contact-stepper" key="CONSENTS">
              <div className="contact-stepper__header">
                <div className="contact-stepper__header--counter"><span>3</span></div>
                <div className="contact-stepper__header--content">
                  <h1 className="contact-stepper__header--content--title"><span>Consentements</span></h1>
                  <p className="contact-stepper__header--content--description">Conformité RGPD</p>
                </div>
              </div>
              <div className="contact-stepper__content">
                <div className="contact-stepper__content--radio">
                  <label className="d-flex align--center gap-8 cursor-pointer">
                    <input type="checkbox" checked={form.optinMarketing} onChange={(e) => updateForm("optinMarketing", e.target.checked)} />
                    <span>J'accepte de recevoir des communications marketing de Kayros et La Fourgale</span>
                  </label>
                </div>
                <div className="contact-stepper__content--radio">
                  <label className="d-flex align--center gap-8 cursor-pointer">
                    <input type="checkbox" checked={form.optinPartners} onChange={(e) => updateForm("optinPartners", e.target.checked)} />
                    <span>J'accepte que mes données soient partagées avec les partenaires</span>
                  </label>
                </div>
                <div className="contact-stepper__content--radio">
                  <label className="d-flex align--center gap-8 cursor-pointer">
                    <input type="checkbox" checked={form.acceptCGV} onChange={(e) => updateForm("acceptCGV", e.target.checked)} />
                    <span>* J'ai lu et j'accepte les Conditions Générales de Vente</span>
                  </label>
                </div>
                <div className="contact-stepper__content--radio">
                  <label className="d-flex align--center gap-8 cursor-pointer">
                    <input type="checkbox" checked={form.acceptSepa} onChange={(e) => updateForm("acceptSepa", e.target.checked)} />
                    <span>* J'autorise Kayros à effectuer des prélèvements conformément au mandat SEPA</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="quote quote--info">
              <div className="quote--description">
                <strong>Récapitulatif: </strong>
                {FORMULA_LABELS[selectedTeleconsultation]} — <strong className="text--primary">{totalPrice.toFixed(2)} €/mois</strong>
              </div>
            </div>
          </Form>
        </div>
        <div className="contact-form__action">
          <div className="d-flex justify--between" style={{ width: "100%" }}>
            <Button onClick={onBack} type="secondary--link">
              <MdArrowBackIos size={16} />
              Retour
            </Button>
            <Button type="primary" onClick={handleSubmit} disabled={!isValid} loading={nextLoading}>
              <MdCheckCircle size={16} />
              Valider la souscription
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contract;
